import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Phone, Mail, CheckCircle2, X, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import COUNTRY_CODES from '../../lib/countryCodes';
import {
  requestPhoneChange,
  verifyPhoneChange,
  resendPhoneChangeOtp,
  extractErrorMessage,
} from '../../api/profile';

/**
 * 3-stage inline dialog for editing the customer's phone.
 *   stage 'enter'  – user inputs the new phone, click Send Code
 *   stage 'otp'    – user enters the 4-digit code emailed to their account
 *   stage 'done'   – success screen, auto-closes
 *
 * Per product decision: the OTP stage cannot be dismissed by clicking
 * outside or pressing Escape (so a stray tap doesn't lose the in-flight
 * change). User can still close via the X button.
 */
export default function PhoneChangeDialog({
  open,
  onOpenChange,
  currentPhone,
  registeredEmail,
  onUpdated,
}) {
  const [stage, setStage] = useState('enter');
  const [countryCode, setCountryCode] = useState('+673');
  const [localPhone, setLocalPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Dedupe by dial code (Radix Select uses `value` as a hidden <option> key)
  const dedupedCountries = useMemo(
    () => Array.from(new Map(COUNTRY_CODES.map((c) => [c.code, c])).values()),
    [],
  );

  // Reset everything whenever the dialog opens/closes
  useEffect(() => {
    if (!open) {
      setStage('enter');
      setCountryCode('+673');
      setLocalPhone('');
      setOtp('');
      setBusy(false);
      setResendCountdown(0);
    }
  }, [open]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const t = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const handleClose = () => {
    if (busy) return;
    onOpenChange(false);
  };

  const handleRequest = async () => {
    const digits = (localPhone || '').replace(/\D/g, '');
    if (digits.length < 7) {
      toast.error('Mobile number should have at least 7 digits');
      return;
    }
    const fullPhone = `${countryCode}${digits}`;
    setBusy(true);
    try {
      await requestPhoneChange(fullPhone);
      toast.success(`Code sent to ${registeredEmail}`);
      setStage('otp');
      setResendCountdown(60);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to send code'));
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 4) {
      toast.error('Enter the 4-digit code');
      return;
    }
    setBusy(true);
    try {
      const res = await verifyPhoneChange(otp);
      setStage('done');
      onUpdated?.(res.user);
      setTimeout(() => onOpenChange(false), 2000);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Invalid or expired code'));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0 || busy) return;
    setBusy(true);
    try {
      await resendPhoneChangeOtp();
      toast.success('New code sent');
      setResendCountdown(60);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not resend code'));
    } finally {
      setBusy(false);
    }
  };

  // Lock dismissal during OTP stage — user must use the X button
  const lockOutsideDismiss = stage === 'otp';

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent
        className="max-w-md"
        data-testid="phone-change-dialog"
        onInteractOutside={(e) => { if (lockOutsideDismiss) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (lockOutsideDismiss) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#7BCB00]" />
            {stage === 'done' ? 'Phone Updated' : 'Change Phone Number'}
          </DialogTitle>
          <DialogDescription>
            {stage === 'enter' && 'Enter the new number. We\u2019ll email a confirmation code to your registered address.'}
            {stage === 'otp' && (
              <>We sent a 4-digit code to <strong>{registeredEmail}</strong>. Enter it below.</>
            )}
            {stage === 'done' && 'Your phone number has been updated successfully.'}
          </DialogDescription>
        </DialogHeader>

        {stage === 'enter' && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase font-bold text-muted-foreground">
                Current phone
              </Label>
              <p className="font-mono text-sm mt-1" data-testid="current-phone-display">
                {currentPhone || '—'}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase font-bold text-muted-foreground mb-1.5 block">
                New phone
              </Label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger
                    data-testid="new-phone-country-code"
                    className="w-[120px]"
                    disabled={busy}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {dedupedCountries.map((c) => (
                      <SelectItem key={c.code + c.country} value={c.code}>
                        <span className="flex items-center gap-2">
                          <span>{c.flag}</span>
                          <span>{c.code}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="new-phone-input"
                  data-testid="new-phone-input"
                  value={localPhone}
                  onChange={(e) => setLocalPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="8XXXXXXX"
                  inputMode="tel"
                  maxLength={15}
                  className="flex-1"
                  disabled={busy}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Enter phone number without country code
              </p>
            </div>
            <Button
              onClick={handleRequest}
              disabled={busy}
              data-testid="send-phone-otp-button"
              className="w-full btn-volt h-12"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Code
            </Button>
          </div>
        )}

        {stage === 'otp' && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" />
              <span>Sent to {registeredEmail}</span>
            </div>
            <div>
              <Label htmlFor="phone-otp-input" className="text-xs uppercase font-bold text-muted-foreground">
                4-digit code
              </Label>
              <Input
                id="phone-otp-input"
                data-testid="phone-otp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="0000"
                inputMode="numeric"
                maxLength={4}
                className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono"
                disabled={busy}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setStage('enter')}
                variant="outline"
                disabled={busy}
                data-testid="phone-otp-back-button"
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Wrong number?
              </Button>
              <Button
                onClick={handleVerify}
                disabled={busy || otp.length !== 4}
                data-testid="verify-phone-otp-button"
                className="flex-1 btn-volt"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verify & Save
              </Button>
            </div>
            <div className="text-center text-sm">
              {resendCountdown > 0 ? (
                <span className="text-muted-foreground">
                  Resend available in {resendCountdown}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={busy}
                  data-testid="resend-phone-otp-button"
                  className="text-[#7BCB00] font-semibold hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              )}
            </div>
          </div>
        )}

        {stage === 'done' && (
          <div
            className="flex flex-col items-center text-center py-6 space-y-3"
            data-testid="phone-change-success"
          >
            <CheckCircle2 className="w-16 h-16 text-emerald-500" />
            <p className="font-mono text-base">{countryCode}{localPhone}</p>
            <p className="text-sm text-muted-foreground">You can close this dialog now.</p>
          </div>
        )}

        {/* Always-visible X button (Radix already renders one, but we add an
            explicit testable one for OTP stage where outside-click is locked) */}
        {lockOutsideDismiss && (
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            aria-label="Close"
            data-testid="phone-dialog-close-x"
            className="absolute top-3 right-3 p-1 rounded hover:bg-muted disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
